/* Natalie Kolyozyan | Homepage Hero Logo / Header Logo controller
 * Uses the actual Hero Logo element's position, not 'is-stuck': Cascade's
 * header becomes sticky almost immediately, while the big logo is still visible.
 * The footer logo cannot trigger this controller. */
(() => {
  'use strict';

  const root = document.documentElement;
  const ACTIVE = 'nk-hero-header-active';
  const PREINIT = 'nk-hero-header-preinit';
  const PASSED = 'nk-hero-header-passed';
  const READY = 'nk-hero-header-motion-ready';
  const INSTANCE_KEY = '__nkHeroHeaderTransition';
  const previous = window[INSTANCE_KEY];
  if (previous && typeof previous.destroy === 'function') previous.destroy();

  const controller = new AbortController();
  const { signal } = controller;
  let frame = 0;
  let motionFrame1 = 0;
  let motionFrame2 = 0;
  let observedHero = null;
  let observedHeader = null;
  let resizeObserver = null;

  function reset() {
    if (motionFrame1) window.cancelAnimationFrame(motionFrame1);
    if (motionFrame2) window.cancelAnimationFrame(motionFrame2);
    motionFrame1 = 0;
    motionFrame2 = 0;
    root.classList.remove(ACTIVE, PASSED, READY, PREINIT);
    if (resizeObserver) resizeObserver.disconnect();
    observedHero = null;
    observedHeader = null;
  }

  function schedule() {
    if (!signal.aborted && !frame) frame = window.requestAnimationFrame(update);
  }

  function enableMotionAfterInitialPaint() {
    if (root.classList.contains(READY) || motionFrame1 || motionFrame2) return;
    motionFrame1 = window.requestAnimationFrame(() => {
      motionFrame1 = 0;
      motionFrame2 = window.requestAnimationFrame(() => {
        motionFrame2 = 0;
        if (!signal.aborted && root.classList.contains(ACTIVE)) root.classList.add(READY);
      });
    });
  }

  function watchSize(heroContent, header) {
    if (!('ResizeObserver' in window)) return;
    if (!resizeObserver) resizeObserver = new ResizeObserver(schedule);
    if (observedHero === heroContent && observedHeader === header) return;
    resizeObserver.disconnect();
    resizeObserver.observe(heroContent);
    resizeObserver.observe(header);
    observedHero = heroContent;
    observedHeader = header;
  }

  function update() {
    frame = 0;
    if (signal.aborted) return;

    try {
      const hero = document.querySelector('[data-nk-hero-logo-trigger]');
      const header = document.querySelector('[data-nk-header-root]');
      const headerLogo = header && header.querySelector('[data-nk-header-logo]');
      if (!hero || !header || !headerLogo || !hero.isConnected || !header.isConnected) {
        reset();
        return;
      }

      const heroContent = hero.querySelector('[data-nk-hero-logo-content]') || hero;
      if (!heroContent.getClientRects().length || !header.getClientRects().length) {
        reset();
        return;
      }

      const heroStyle = window.getComputedStyle(hero);
      if (heroStyle.display === 'none' || heroStyle.visibility === 'hidden') {
        reset();
        return;
      }

      const heroRect = heroContent.getBoundingClientRect();
      const headerRect = header.getBoundingClientRect();
      if (heroRect.height <= 0 || heroRect.width <= 0) {
        reset();
        return;
      }

      // '0' means after the entire large Hero Logo has left the top of the
      // viewport. Positive merchant-controlled values reveal slightly earlier.
      const offset = Math.max(0, Math.min(120,
        parseFloat(hero.dataset.nkHeaderLogoRevealOffset) || 0));
      const revealLine = Math.max(0, headerRect.top) + offset;
      const passed = heroRect.bottom <= revealLine;

      // Never hide a logo based on sticky state or an arbitrary scroll distance.
      root.classList.toggle(PASSED, passed);
      root.classList.add(ACTIVE);
      // Release first-paint guard only after the correct visibility is applied.
      root.classList.remove(PREINIT);
      enableMotionAfterInitialPaint();
      watchSize(heroContent, header);
    } catch (error) {
      // If the theme changes its DOM, prefer the original visible header logo.
      reset();
      if (window.console && typeof window.console.warn === 'function') {
        window.console.warn('NK Hero/Header logo transition:', error);
      }
    }
  }

  function destroy() {
    controller.abort();
    if (frame) window.cancelAnimationFrame(frame);
    reset();
    if (window[INSTANCE_KEY] && window[INSTANCE_KEY].destroy === destroy) {
      delete window[INSTANCE_KEY];
    }
  }

  window[INSTANCE_KEY] = { destroy };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', update, { once: true, signal });
  } else {
    update();
  }

  window.addEventListener('load', schedule, { signal });
  window.addEventListener('pageshow', schedule, { signal });
  window.addEventListener('scroll', schedule, { passive: true, signal });
  window.addEventListener('resize', schedule, { passive: true, signal });
  window.addEventListener('header-stick', schedule, { signal });
  window.addEventListener('header-unstick', schedule, { signal });
  document.addEventListener('visibilitychange', schedule, { signal });
  document.addEventListener('shopify:section:load', schedule, { signal });
  document.addEventListener('shopify:section:reorder', schedule, { signal });
  document.addEventListener('shopify:section:select', schedule, { signal });
  document.addEventListener('shopify:section:unload', () => {
    reset();
    schedule();
  }, { signal });
})();
